/**
 * Issue a heat to individual piece marks (PMK). CR-2026-PIECE-TRACE (2026-10-14).
 * Server: /api/piece-trace (sfm-web-backend src/routes/pieceTrace.js).
 *
 * Material issue is the traceability capture point (spec §9.2): the heat is issued to each physical piece
 * ("03 of 24"), not to the mark as a whole. Rules are the server's: released heat only, the heat's own job only,
 * at most 50 pieces at a time, a second heat on one piece needs a reason (splice), links are never edited or removed.
 * CR-2026-GATE-OVERRIDE (2026-10-14): a QUARANTINE or AT_RISK heat issues only under a co-signed HEAT_ISSUE override
 * (Admin request, QC co-sign), once, to the pieces it names. Grade mismatch has no override.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Card, Checkbox, Chip, MenuItem, Stack, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ApiCall from "../../utils/apicall";
import { usableHeatOverrides } from "../../pages/GateOverrides/overrideModel";
import { hasPermission } from "../../utils/hasPermission";

import SmartTable from "../SmartTable/SmartTable";
export const MAX_PIECES_PER_ISSUE = 50;

export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`.slice(0, 36);
}

/** The POST /piece-trace/issue body (exported for tests). */
export function issueBody(heatId, pieceIds, reason, key, overrideId) {
  const b = { heatId: Number(heatId), pieceIds: pieceIds.map(Number), idempotencyKey: key };
  if ((reason || "").trim()) b.reason = reason.trim();
  if (overrideId) b.overrideId = Number(overrideId);
  return b;
}

/** Pieces in the selection that already carry a different heat (the server asks for a reason). */
export function secondHeatPieces(pieces, selected, heatId) {
  return pieces.filter((p) => selected.includes(p.id) && p.heats.length > 0 && !p.heats.some((h) => Number(h.heatId) === Number(heatId)));
}

const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;

export default function PieceIssuePanel({ heat }) {
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const canIssue = hasPermission(perms, "edit_heat");
  const [marks, setMarks] = useState([]);
  const [itemId, setItemId] = useState("");
  const [pieces, setPieces] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [issuedFromHeat, setIssuedFromHeat] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const released = String(heat?.status || "").toUpperCase() === "RELEASED";
  const overridable = ["QUARANTINE", "AT_RISK"].includes(String(heat?.status || "").toUpperCase());
  const canRequestOverride = hasPermission(perms, "request_gate_override");
  const [overrides, setOverrides] = useState([]);
  const [overrideId, setOverrideId] = useState("");
  const override = overrides.find((o) => String(o.id) === String(overrideId)) || null;

  const loadOverrides = useCallback(async () => {
    if (!heat?.id || !overridable) { setOverrides([]); return; }
    try { const r = await ApiCall.get(`/gate-overrides?status=COSIGNED&jobId=${heat.jobId}`); setOverrides(usableHeatOverrides(r.data.data, heat.id)); }
    catch (_) { setOverrides([]); }
  }, [heat?.id, heat?.jobId, overridable]);
  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  const loadIssued = useCallback(async () => {
    if (!heat?.id) return;
    try { const r = await ApiCall.get(`/piece-trace/heats/${heat.id}/pieces`); setIssuedFromHeat(r.data.data || []); } catch (_) { setIssuedFromHeat([]); }
  }, [heat?.id]);

  useEffect(() => {
    if (!heat?.jobId) return;
    ApiCall.get(`/fabricated-items/job/${heat.jobId}`)
      .then((r) => setMarks(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setMarks([]));
    loadIssued();
  }, [heat?.jobId, loadIssued]);

  const loadPieces = useCallback(async (id) => {
    setPieces([]); setSelected([]); setReason("");
    if (!id) return;
    try { const r = await ApiCall.get(`/piece-trace/items/${id}/pieces`); setPieces((r.data.data || []).filter((p) => p.status === "ACTIVE")); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load the pieces.") }); }
  }, []);
  useEffect(() => { loadPieces(itemId); }, [itemId, loadPieces]);

  const second = useMemo(() => secondHeatPieces(pieces, selected, heat?.id), [pieces, selected, heat?.id]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= MAX_PIECES_PER_ISSUE ? s : [...s, id]));
  const selectable = pieces.filter((p) => !p.heats.some((h) => Number(h.heatId) === Number(heat?.id)));

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const ids = released ? selected : (override ? override.pieceIds : []);
      const r = await ApiCall.post("/piece-trace/issue", issueBody(heat.id, ids, reason, newIdempotencyKey(), released ? null : overrideId));
      setMsg({ severity: "success", text: r.data?.message || "Issued." });
      await loadPieces(itemId); await loadIssued();
      if (!released) { setOverrideId(""); await loadOverrides(); }
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not issued.") }); } finally { setBusy(false); }
  };

  if (!heat) return null;
  return (
    <Card sx={{ mt: 3, p: 2 }}>
      <Typography variant="h6">Issue to individual pieces (PMK)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Material issue records which heat went into which physical piece. Required before release and station work on jobs whose
        contract requires heat to piece mark traceability.
      </Typography>
      {!released && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This heat is {heat.status}. Only a released heat can be issued; QC releases it first.
          {overridable && " Otherwise it needs an Admin override co-signed by QC, used once for the pieces it names."}
          {overridable && canRequestOverride && (
            <Button size="small" component={RouterLink} to={`/gate-overrides?type=HEAT_ISSUE&jobId=${heat.jobId}&heatId=${heat.id}`} sx={{ ml: 1 }}>Request an override</Button>
          )}
        </Alert>
      )}
      {!released && overrides.length > 0 && canIssue && (
        <TextField select size="small" label="Co-signed override" value={overrideId} onChange={(e) => setOverrideId(e.target.value)} sx={{ minWidth: 420, mb: 1.5 }}>
          <MenuItem value="">Choose an override</MenuItem>
          {overrides.map((o) => (
            <MenuItem key={o.id} value={String(o.id)}>#{o.id}: {o.pieceIds.length} piece(s), requested by {o.requestedByName}, co-signed by {o.cosignedByName}</MenuItem>
          ))}
        </TextField>
      )}
      {!released && override && canIssue && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Override #{override.id} issues heat {heat.heatNumber} to the {override.pieceIds.length} piece{override.pieceIds.length === 1 ? "" : "s"} it names, in one issue.
            It is then used up. Reason on record: {override.reason}
          </Typography>
          <TextField fullWidth size="small" sx={{ mb: 1 }} label="Second-heat reason, only if a named piece already carries another heat (e.g. splice per drawing)"
            value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button variant="contained" color="warning" disabled={busy} onClick={submit}>
            Issue heat {heat.heatNumber} under override #{override.id}
          </Button>
        </Box>
      )}
      {msg && <Alert severity={msg.severity} sx={{ mb: 1.5 }}>{msg.text}</Alert>}
      {canIssue && released && (
        <Box sx={{ mb: 2 }}>
          <TextField select size="small" label="Mark" value={itemId} onChange={(e) => setItemId(e.target.value)} sx={{ minWidth: 260 }}>
            <MenuItem value="">Choose a mark</MenuItem>
            {marks.map((m) => <MenuItem key={m.id} value={m.id}>{m.name} (qty {m.quantity})</MenuItem>)}
          </TextField>
          {pieces.length > 0 && (
            <>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button size="small" onClick={() => setSelected(selectable.slice(0, MAX_PIECES_PER_ISSUE).map((p) => p.id))}>Select all not issued this heat</Button>
                <Button size="small" onClick={() => setSelected([])}>Clear</Button>
                <Typography variant="caption" sx={{ alignSelf: "center" }}>{selected.length} selected (max {MAX_PIECES_PER_ISSUE})</Typography>
              </Stack>
              <SmartTable tableId="piece-issue" lockedColumns={["Piece", "Heats issued"]} size="small" sx={{ mt: 1 }}>
                <TableHead><TableRow><TableCell padding="checkbox" /><TableCell>Piece</TableCell><TableCell>Identity</TableCell><TableCell>Heats issued</TableCell></TableRow></TableHead>
                <TableBody>
                  {pieces.map((p) => {
                    const has = p.heats.some((h) => Number(h.heatId) === Number(heat.id));
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" disabled={has || busy} checked={selected.includes(p.id)} onChange={() => toggle(p.id)}
                            inputProps={{ "aria-label": `Select ${p.mark} ${p.display}` }} />
                        </TableCell>
                        <TableCell>{p.mark} {p.display}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>{p.publicId}</TableCell>
                        <TableCell>
                          {p.heats.length === 0 ? <Typography variant="caption" color="error">None</Typography>
                            : p.heats.map((h) => <Chip key={h.heatId} size="small" sx={{ mr: 0.5 }} label={`${h.heatNumber}${h.confirmedAtCut ? " (confirmed at cut)" : ""}`} />)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </SmartTable>
              {second.length > 0 && (
                <TextField fullWidth size="small" sx={{ mt: 1.5 }} required label="Reason for a second heat on the same piece (e.g. splice per drawing)"
                  value={reason} onChange={(e) => setReason(e.target.value)} />
              )}
              <Button variant="contained" sx={{ mt: 1.5 }} disabled={busy || !selected.length || (second.length > 0 && reason.trim().length < 4)} onClick={submit}>
                Issue heat {heat.heatNumber} to {selected.length} piece{selected.length === 1 ? "" : "s"}
              </Button>
            </>
          )}
        </Box>
      )}
      <Typography variant="subtitle2" sx={{ mt: 1 }}>Pieces issued from this heat ({issuedFromHeat.length})</Typography>
      {issuedFromHeat.length === 0 ? (
        <Typography variant="caption" color="text.secondary">None yet.</Typography>
      ) : (
        <SmartTable tableId="piece-issued" lockedColumns={["Piece", "Issued", "At cut"]} size="small">
          <TableHead><TableRow><TableCell>Piece</TableCell><TableCell>Identity</TableCell><TableCell>Issued</TableCell><TableCell>At cut</TableCell></TableRow></TableHead>
          <TableBody>
            {issuedFromHeat.map((p) => {
              const h = p.heats.find((x) => Number(x.heatId) === Number(heat.id)) || {};
              return (
                <TableRow key={p.id}>
                  <TableCell>{p.mark} {p.display}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{p.publicId}</TableCell>
                  <TableCell>{String(h.issuedAt || "").slice(0, 10)} {h.issuedBy || ""}{h.reason ? ` (${h.reason})` : ""}</TableCell>
                  <TableCell>{h.confirmedAtCut ? "Confirmed" : "Not yet"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </SmartTable>
      )}
    </Card>
  );
}
